import { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useUserStore from './UserStore';
import { useEffect } from 'react';
import { apiUrl } from './config';

export default function CustomerAddForm() {
  const navigate = useNavigate();
  const { customerId } = useParams(); // Get customerId from route parameters
  const location = useLocation();
  const user = useUserStore((state) => state.user);
  
  // State to track if we're in edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    account_id: '',
    name: '',
    billing_name: '',
    account_rep: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    telephone: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    // Billing address fields
    billing_address_1: '',
    billing_address_2: '',
    billing_city: '',
    billing_state: '',
    billing_zip_code: '',
    // Billing contact fields
    billing_contact_name: '',
    billing_contact_email: '',
    billing_contact_phone: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBillingAddress, setShowBillingAddress] = useState(false);
  const delegatedCustomerName = new URLSearchParams(location.search).get('name') || '';

  // Check for customerId and determine mode (add vs edit)
  useEffect(() => {
    const initializeForm = async () => {
      if (customerId) {
        // Edit mode - fetch existing customer data
        setIsEditMode(true);
        setIsLoading(true);
        
        try {
          console.log('Loading customer for edit, ID:', customerId);
          const response = await fetch(apiUrl(`/customer/one?customerId=${customerId}`));
          
          if (response.ok) {
            const customerData = await response.json();
            console.log('Customer data loaded:', customerData);
            
            // Populate form with existing data
            setFormData({
              account_id: customerData.account_id || '',
              name: customerData.name || '',
              billing_name: customerData.billing_name || '',
              account_rep: customerData.account_rep || '',
              address_1: customerData.address_1 || '',
              address_2: customerData.address_2 || '',
              city: customerData.city || '',
              state: customerData.state || '',
              zip_code: customerData.zip_code || '',
              telephone: customerData.telephone || '',
              contact_name: customerData.contact_name || '',
              contact_email: customerData.contact_email || '',
              contact_phone: customerData.contact_phone || '',
              // Billing address fields
              billing_address_1: customerData.billing_address_1 || '',
              billing_address_2: customerData.billing_address_2 || '',
              billing_city: customerData.billing_city || '',
              billing_state: customerData.billing_state || '',
              billing_zip_code: customerData.billing_zip_code || '',
              // Billing contact fields
              billing_contact_name: customerData.billing_contact_name || '',
              billing_contact_email: customerData.billing_contact_email || '',
              billing_contact_phone: customerData.billing_contact_phone || '',
            });
            
            // Show billing address section if any billing fields have data
            const hasBillingData = customerData.billing_address_1 || 
                                 customerData.billing_city || 
                                 customerData.billing_state || 
                                 customerData.billing_zip_code ||
                                 customerData.billing_contact_name ||
                                 customerData.billing_contact_email ||
                                 customerData.billing_contact_phone;
            
            setShowBillingAddress(!!hasBillingData);
            
          } else {
            console.error('Failed to load customer data');
            // Optionally redirect or show error message
            navigate('/customers');
          }
        } catch (error) {
          console.error('Error loading customer:', error);
          // Optionally redirect or show error message
          navigate('/customers');
        } finally {
          setIsLoading(false);
        }
      } else {
        // Add mode - start with empty form
        setIsEditMode(false);
        if (delegatedCustomerName.trim()) {
          setFormData((prev) => ({
            ...prev,
            name: delegatedCustomerName.trim(),
          }));
        }
        console.log('Form initialized in ADD mode');
      }
    };

    initializeForm();
  }, [customerId, navigate, delegatedCustomerName]);

  // Auto-populate account representative from user data (only in add mode)
  useEffect(() => {
    if (!isEditMode && user.user) {
      console.log("user data in customer add form:", user);

      const accountRep = user.user.name || 
                        user.user.displayName || 
                        user.user.full_name || 
                        `${user.user.first_name || ''} ${user.user.last_name || ''}`.trim() ||
                        user.user.email?.split('@')[0] || 
                        '';
      
      setFormData(prev => ({
        ...prev,
        account_rep: accountRep
      }));
    }
  }, [user, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBillingAddressToggle = (e) => {
    const isChecked = e.target.checked;
    setShowBillingAddress(isChecked);
    
    // Clear billing address fields if unchecked
    if (!isChecked) {
      setFormData(prev => ({
        ...prev,
        billing_address_1: '',
        billing_address_2: '',
        billing_city: '',
        billing_state: '',
        billing_zip_code: '',
        billing_contact_name: '',
        billing_contact_email: '',
        billing_contact_phone: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const customerData = {
        _id: customerId || undefined, // Include _id for edits
        ...formData,
        modified_date: new Date().toISOString(),
        // Only add create_date for new customers
        ...(isEditMode ? {} : { create_date: new Date().toISOString(), term_date: "" })
      };

      console.log(`${isEditMode ? 'Updating' : 'Creating'} customer data:`, customerData);

      const url = isEditMode 
        ? apiUrl(`/customer/update`) 
        : apiUrl('/customer/create');
      
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customerData),
      });

      if (response.ok) {
        console.log(`Customer ${isEditMode ? 'updated' : 'added'} successfully`);
        navigate('/customers');
      } else {
        console.error(`Failed to ${isEditMode ? 'update' : 'add'} customer`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} customer:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/customers');
  };

  // Show loading state while fetching customer data
  if (isLoading) {
    return (
      <div className="lg:pl-72 m-3 sm:ml-0 px-0 sm:px-0">
        <div className="text-center py-12">
          <div className="text-gray-500">Loading customer data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:pl-72 m-3 sm:ml-0 px-0 sm:px-0">
      <form onSubmit={handleSubmit}>
        <div className="space-y-12 sm:space-y-16">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              {isEditMode ? 'Edit Customer' : 'Add Customer'}
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
              Customer information for Bandtango and Bandtango related Properties 
            </p>

            <div className="mt-10 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0">
              
              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Company Name *
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xl sm:text-sm sm:leading-6"
                    placeholder="Jaimes Granite Construction"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="billing_name" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Billing Name
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <input
                    type="text"
                    name="billing_name"
                    id="billing_name"
                    value={formData.billing_name}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xl sm:text-sm sm:leading-6"
                    placeholder="Same as company name if different"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="account_rep" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Account Representative
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <div className="flex">
                    <input
                      type="text"
                      name="account_rep"
                      id="account_rep"
                      value={formData.account_rep}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                      placeholder="Dallas Stevens"
                    />
                    {user && !isEditMode && (
                      <div className="ml-2 flex items-center text-xs text-gray-500">
                        (Auto-filled from your profile)
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">Company Address Information</h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
              Primary business address where your company can receive mail.
            </p>

            <div className="mt-10 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0">
              
              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="address_1" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Street Address
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <input
                    type="text"
                    name="address_1"
                    id="address_1"
                    value={formData.address_1}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xl sm:text-sm sm:leading-6"
                    placeholder="3101 Agnes Suite A-5"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="address_2" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Address Line 2
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <input
                    type="text"
                    name="address_2"
                    id="address_2"
                    value={formData.address_2}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xl sm:text-sm sm:leading-6"
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="city" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  City
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <input
                    type="text"
                    name="city"
                    id="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    placeholder="Corpus Christi"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="state" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  State / Province
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <input
                    type="text"
                    name="state"
                    id="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    placeholder="TX"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="zip_code" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  ZIP / Postal Code
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <input
                    type="text"
                    name="zip_code"
                    id="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    placeholder="78405"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="telephone" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Telephone
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <input
                    type="tel"
                    name="telephone"
                    id="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    placeholder="361-548-0410"
                  />
                </div>
              </div>

              {/* Billing Address Checkbox */}
              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <div className="sm:col-span-3">
                  <div className="flex items-center">
                    <input
                      id="show-billing-address"
                      name="show-billing-address"
                      type="checkbox"
                      checked={showBillingAddress}
                      onChange={handleBillingAddressToggle}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label htmlFor="show-billing-address" className="ml-2 block text-sm font-medium leading-6 text-gray-900">
                      Billing address is different from company address
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Billing Address Section - Only visible when checkbox is checked */}
          {showBillingAddress && (
            <div>
              <h2 className="text-base font-semibold leading-7 text-gray-900">Billing Address Information</h2>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
                Address where invoices and billing information should be sent.
              </p>

              <div className="mt-10 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0">
                
                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                  <label htmlFor="billing_address_1" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    Billing Street Address
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <input
                      type="text"
                      name="billing_address_1"
                      id="billing_address_1"
                      value={formData.billing_address_1}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xl sm:text-sm sm:leading-6"
                      placeholder="P.O. Box 1234 or different street address"
                    />
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                  <label htmlFor="billing_address_2" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    Billing Address Line 2
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <input
                      type="text"
                      name="billing_address_2"
                      id="billing_address_2"
                      value={formData.billing_address_2}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xl sm:text-sm sm:leading-6"
                      placeholder="Suite, apartment, etc. (optional)"
                    />
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                  <label htmlFor="billing_city" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    Billing City
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <input
                      type="text"
                      name="billing_city"
                      id="billing_city"
                      value={formData.billing_city}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                      placeholder="Houston"
                    />
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                  <label htmlFor="billing_state" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    Billing State / Province
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <input
                      type="text"
                      name="billing_state"
                      id="billing_state"
                      value={formData.billing_state}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                      placeholder="TX"
                    />
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                  <label htmlFor="billing_zip_code" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    Billing ZIP / Postal Code
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <input
                      type="text"
                      name="billing_zip_code"
                      id="billing_zip_code"
                      value={formData.billing_zip_code}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                      placeholder="77001"
                    />
                  </div>
                </div>

                {/* Billing Contact Information */}
                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                  <label htmlFor="billing_contact_name" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    Billing Contact Name
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <input
                      type="text"
                      name="billing_contact_name"
                      id="billing_contact_name"
                      value={formData.billing_contact_name}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xl sm:text-sm sm:leading-6"
                      placeholder="Billing department contact person"
                    />
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                  <label htmlFor="billing_contact_email" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    Billing Contact Email
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <input
                      type="email"
                      name="billing_contact_email"
                      id="billing_contact_email"
                      value={formData.billing_contact_email}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-md sm:text-sm sm:leading-6"
                      placeholder="billing@company.com"
                    />
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                  <label htmlFor="billing_contact_phone" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    Billing Contact Phone
                  </label>
                  <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <input
                      type="tel"
                      name="billing_contact_phone"
                      id="billing_contact_phone"
                      value={formData.billing_contact_phone}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                      placeholder="361-555-9876"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">Contact Information</h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
              Primary contact person and email for this account.
            </p>

            <div className="mt-10 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0">
              
              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="contact_name" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Contact Name
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <input
                    type="text"
                    name="contact_name"
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xl sm:text-sm sm:leading-6"
                    placeholder="Primary contact person"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="contact_email" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Contact Email
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <input
                    type="email"
                    name="contact_email"
                    id="contact_email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-md sm:text-sm sm:leading-6"
                    placeholder="contact@company.com"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label htmlFor="contact_phone" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Contact Phone
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <input
                    type="tel"
                    name="contact_phone"
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    placeholder="361-555-1234"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-x-6">
          <button 
            type="button" 
            onClick={handleCancel}
            className="text-sm font-medium leading-6 text-gray-900"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
          >
            {isSubmitting ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update Customer' : 'Save Customer')}
          </button>
        </div>
      </form>    
    </div>
  )
}